<template>
  <div class="pb-container">
    <div v-if="loading.powerBI || loading.exportVisual">
      <IconLoading :loadingText="loading.loadingText"></IconLoading>
    </div>
    <div
      id="embedContainer"
      class="embed-container"
      :class="reportDisplayCss">
    </div>
    <div v-if="isReportLoadError" class="load-error">Unable to retrieve report</div>
  </div>
</template>

<script setup>
import apiManager from '@/api';
import {
  reactive, ref, onMounted, computed, inject,
} from 'vue';
import { openFile } from '@/utils/blobOptions';
import * as pbi from 'powerbi-client';
import IconLoading from '../loaders/IconLoading.vue';
// eslint-disable-next-line import/no-extraneous-dependencies

const moment = inject('moment');

const loading = reactive({
  powerBI: false,
  exportVisual: false,
  loadingText: 'Loading Report',
});

const props = defineProps({
  reportName: {
    type: String,
    required: true,
  },
});
// -------------------- POWER BI -------------------

const embedAccessToken = ref();
const isReportLoadError = ref(false);
const reportDisplayCss = computed(() => {
  if (loading.powerBI) {
    return 'report-display-none';
  }
  return '';
});

let loadedResolve;
// eslint-disable-next-line no-unused-vars
const reportLoaded = new Promise((res) => { loadedResolve = res; });

let renderedResolve;
// eslint-disable-next-line no-unused-vars
const reportRendered = new Promise((res) => { renderedResolve = res; });

function constructFileName(visualElementName, fileExtension) {
  return `${moment().format('YYYYMMDD_HHmmss_SSS')}_${visualElementName.replace(' ', '_')}.${fileExtension}`;
}

async function printSpecificVisual(pageName, visualName, visualTitle) {
  loading.loadingText = 'Exporting Visual';
  loading.exportVisual = true;
  const response = await apiManager.powerbi.outputVisual(props.reportName, pageName, visualName);
  const fileName = constructFileName(visualTitle, 'png');
  openFile(fileName, response);
  loading.exportVisual = false;
}

async function embedPowerBIReport() {
  loading.powerBI = true;
  let embedTokenObject = null;

  try {
    embedTokenObject = await apiManager.powerbi.embedToken(props.reportName);
  } catch {
    isReportLoadError.value = true;
    return;
  }

  if (!embedTokenObject.token) {
    loading.powerBI = true;
    return;
  }

  embedAccessToken.value = embedTokenObject.token;

  const config = {
    type: 'report',
    tokenType: pbi.models.TokenType.Embed,
    accessToken: embedAccessToken.value,
    embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${embedTokenObject.powerBIReportList[0].reportID}`,
    permissions: pbi.models.Permissions.All,
    settings: {
      LayoutType: pbi.models.LayoutType.Custom,
      customLayout: {
        displayOption: pbi.models.DisplayOption.FitToWidth,
      },
      filterPaneEnabled: false,
      navContentPaneEnabled: false,
    },
    panes: {
      filters: {
        visible: false,
      },
      pageNavigation: {
        visible: true,
      },
    },
    bars: {
      statusBar: {
        visible: true,
      },
    },
  };

  const embedContainer = document.getElementById('embedContainer');

  const powerbiInstance = new pbi.service.Service(
    pbi.factories.hpmFactory,
    pbi.factories.wpmpFactory,
    pbi.factories.routerFactory,
  );

  // Embed the report and display it within the div container.
  const report = powerbiInstance.embed(embedContainer, config);

  // report.off removes all event handlers for a specific event
  report.off('loaded');

  // report.on will add an event handler
  report.on('loaded', () => {
    loadedResolve();
    report.off('loaded');
    loading.powerBI = false;
  });

  // report.off removes all event handlers for a specific event
  report.off('error');

  report.on('error');

  // report.off removes all event handlers for a specific event
  report.off('rendered');

  // report.on will add an event handler
  report.on('rendered', () => {
    renderedResolve();
    report.off('rendered');
  });

  // report.off removes all event handlers for a specific event
  report.off('buttonClicked');

  // report.on will add an event listener.
  report.on('buttonClicked', async (event) => {
    if (event.detail.type === 'WebUrl') {
      apiManager.analytic.add({
        analyticTypeName: 'ButtonClick',
        analyticSource: `Export ${event.detail.title}`,
      });
      try {
        const pages = await report.getPages();

        const activePage = pages?.find((page) => page.isActive);

        // eslint-disable-next-line no-unused-vars
        const visuals = await activePage?.getVisuals();

        // const activeVisual = visuals
        const visualElement = event.detail.title.replace('Export ', '');

        const visual = visuals?.find((item) => item.title === visualElement);

        if (activePage && visual) {
          printSpecificVisual(activePage.name, visual.name, visual.title);
        }
      } catch (errors) {
        // eslint-disable-next-line no-console
        console.log(errors);
      }
    }
  });
}

onMounted(() => {
  embedPowerBIReport();
});
</script>

<style scoped>
  .pb-container {
    margin-top: 20px;
    width: 95%;
    margin-left: auto;
    margin-right: auto;
  }

  .embed-container {
    height: 80vh
  }

  #embedContainer {
    text-align: left;
  }

  #embedContainer iframe {
    border: none; /* Optional: Removes the border around the iframe */
    width: 100%; /* Ensures the iframe takes full width of its container */
    height: 100%; /* Ensures the iframe takes full height of its container */
  }
  .load-error {
    font-size: 1.5rem;
  }

  .report-display-none {
    display: none;
  }

</style>
